import {BibtexSource} from "bibtex-parse-js";
import ISource, {IAnthology, IArticle, IBookContribution, IMonograph, IPaper, IWebsite} from "../interfaces/ISource";
import ISourceType from "../interfaces/ISourceType";
import IUsability from "../interfaces/IUsability";

function sanitizeString(raw: string): string {
    return raw
        .replace(/[{}]/gi, '')
        .replace(/\r?\n|\r/g, '')
        .replace(/\s\s+/g, ' ')
        .trim();
}

function extractAbstract(source: BibtexSource): string {
    const abstract = source.entryTags.abstract || source.entryTags.Abstract || '';

    return sanitizeString(abstract);
}

function extractAuthors(source: BibtexSource): string[] {
    const authors = source.entryTags.author || source.entryTags.Author || '';
    const sanitizedAuthors = sanitizeString(authors);
    const sanitizedAuthorsList = sanitizedAuthors.split('and');

    return sanitizedAuthorsList.map(author => author.trim());
}

function extractTitle(source: BibtexSource): string {
    return sanitizeString(source.entryTags.title || source.entryTags.Title || '');
}

function extractYear(source: BibtexSource): number {
    const year = source.entryTags.year || source.entryTags.Year || '-1';
    const sanitizedYear = sanitizeString(year);

    return parseInt(sanitizedYear, 10);
}

function extractKeywords(source: BibtexSource): string[] {
    const keywords = source.entryTags.keywords || source.entryTags.Keywords || source.entryTags.author_keywords || '';

    const bySemicolon = keywords.split(';');

    if (bySemicolon.length > 1) {
        return bySemicolon.map(keyword => sanitizeString(keyword));
    }

    const byComma = keywords.split(',');

    if (byComma.length > 1) {
        return byComma.map(keyword => sanitizeString(keyword));
    }

    return [sanitizeString(keywords)];
}

function extractJournal(source: BibtexSource): string {
    return sanitizeString(source.entryTags.Journal || source.entryTags.journal || 'N/A');
}

function extractNumber(source: BibtexSource): string {
    return sanitizeString(source.entryTags.Number || source.entryTags.number || 'N/A');
}

function extractVolume(source: BibtexSource): string {
    return sanitizeString(source.entryTags.Volume || source.entryTags.volume || 'N/A');
}

function extractVolumeTitle(source: BibtexSource): string {
    return "not implemented";
}

function extractPages(source: BibtexSource): string {
    return sanitizeString(source.entryTags.Pages || source.entryTags.pages || 'N/A');
}

function extractPublisher(source: BibtexSource): string | undefined {
    const publisher = source.entryTags.Publisher || source.entryTags.publisher;

    if (!publisher) {
        return undefined;
    }

    return sanitizeString(publisher);
}

function extractLocation(source: BibtexSource): string | undefined {
    const location = source.entryTags.Location || source.entryTags.location;

    if (!location) {
        return undefined;
    }

    return sanitizeString(location);
}

function extractISBN(source: BibtexSource): string | undefined {
    return sanitizeString(source.entryTags.ISBN || source.entryTags.isbn || 'N/A');
}

function extractISSN(source: BibtexSource): string | undefined {
    return sanitizeString(source.entryTags.ISSN || source.entryTags.issn || 'N/A');
}

function extractDOI(source: BibtexSource): string | undefined {
    const doi = source.entryTags.DOI || source.entryTags.doi;

    if (!doi) {
        return undefined;
    }

    return sanitizeString(doi);
}

function extractEdition(source: BibtexSource): string | undefined {
    const edition = source.entryTags.Edition || source.entryTags.edition;

    if (!edition) {
        return undefined;
    }

    return sanitizeString(edition);
}

function extractAccessDate(source: BibtexSource): Date {
    return new Date();
}

function extractPageName(source: BibtexSource): string {
    return "not implemented";
}

function extractURI(source: BibtexSource): URL {
    const uri = sanitizeString(source.entryTags.URI || source.entryTags.uri || '');
    return new URL(uri);
}

const typeTranslationTable: Array<{ key: string, type: ISourceType }> = [
    {key: "ARTICLE", type: ISourceType.Article},
    {key: "BOOK", type: ISourceType.Bookcontribution},
    {key: "BOOKLET", type: ISourceType.Bookcontribution},
    {key: "CONFERENCE", type: ISourceType.Article},
    {key: "INBOOK", type: ISourceType.Bookcontribution},
    {key: "INCOLLECTION", type: ISourceType.Bookcontribution},
    {key: "INPROCEEDINGS", type: ISourceType.Article},
    {key: "MANUAL", type: ISourceType.Bookcontribution},
    {key: "MASTERSTHESIS", type: ISourceType.Paper},
    {key: "PHDTHESIS", type: ISourceType.Paper},
    {key: "PROCEEDINGS", type: ISourceType.Article},
    {key: "TECHREPORT", type: ISourceType.Article},
    {key: "UNPUBLISHED", type: ISourceType.Article},
];

function extractType(source: BibtexSource): ISourceType {
    for (const type of typeTranslationTable) {
        if (source.entryType.toUpperCase() === type.key) {
            return type.type;
        }
    }

    return ISourceType.Anthology;
}

export function createSource(raw: BibtexSource): ISource {
    const type = extractType(raw);

    switch (type) {
        case ISourceType.Anthology: {
            const anthology: IAnthology = {
                abstract: extractAbstract(raw),
                authors: extractAuthors(raw),
                keywords: extractKeywords(raw),
                title: extractTitle(raw),
                type,
                usability: IUsability.Unrated,
                year: extractYear(raw),
            };

            const location = extractLocation(raw);
            if (location) {
                anthology.location = location;
            }

            const isbn = extractISBN(raw);
            if (isbn) {
                anthology.isbn = isbn;
            }

            const doi = extractDOI(raw);
            if (doi) {
                anthology.doi = doi;
            }

            return anthology;
        }

        case ISourceType.Article: {
            const article: IArticle = {
                abstract: extractAbstract(raw),
                authors: extractAuthors(raw),
                journal: extractJournal(raw),
                keywords: extractKeywords(raw),
                number: extractNumber(raw),
                pages: extractPages(raw),
                title: extractTitle(raw),
                type,
                usability: IUsability.Unrated,
                volume: extractVolume(raw),
                year: extractYear(raw)
            };

            const issn = extractISSN(raw);
            if (issn) {
                article.issn = issn;
            }

            const doi = extractDOI(raw);
            if (doi) {
                article.doi = doi;
            }

            return article;
        }

        case ISourceType.Bookcontribution: {
            const bookContribution: IBookContribution = {
                abstract: extractAbstract(raw),
                authors: extractAuthors(raw),
                bookTitle: extractTitle(raw),
                keywords: extractKeywords(raw),
                title: extractTitle(raw),
                type,
                usability: IUsability.Unrated,
                year: extractYear(raw)
            };

            return bookContribution;
        }

        case ISourceType.Monograph: {
            const monograph: IMonograph = {
                abstract: extractAbstract(raw),
                authors: extractAuthors(raw),
                keywords: extractKeywords(raw),
                publisher: extractPublisher(raw) || "",
                title: extractTitle(raw),
                type,
                usability: IUsability.Unrated,
                year: extractYear(raw)
            };

            const edition = extractEdition(raw);
            if (edition) {
                monograph.edition = edition;
            }

            const location = extractLocation(raw);
            if (location) {
                monograph.location = location;
            }

            const isbn = extractISBN(raw);
            if (isbn) {
                monograph.isbn = isbn;
            }

            const doi = extractDOI(raw);
            if (doi) {
                monograph.doi = doi;
            }

            return monograph;
        }

        case ISourceType.Paper: {
            const paper: IPaper = {
                abstract: extractAbstract(raw),
                authors: extractAuthors(raw),
                keywords: extractKeywords(raw),
                pages: extractPages(raw),
                title: extractTitle(raw),
                type,
                usability: IUsability.Unrated,
                volumeTitle: extractVolumeTitle(raw),
                year: extractYear(raw)
            };

            const publisher = extractPublisher(raw);
            if (publisher) {
                paper.publisher = publisher;
            }

            const location = extractLocation(raw);
            if (publisher) {
                paper.location = location;
            }

            const doi = extractDOI(raw);
            if (publisher) {
                paper.doi = doi;
            }

            return paper;
        }

        case ISourceType.Website: {
            const website: IWebsite = {
                abstract: extractAbstract(raw),
                accessDate: extractAccessDate(raw),
                authors: extractAuthors(raw),
                doi: extractDOI(raw) || "",
                keywords: extractKeywords(raw),
                pageName: extractPageName(raw),
                title: extractTitle(raw),
                type,
                uri: extractURI(raw),
                usability: IUsability.Unrated,
                year: extractYear(raw)
            };

            return website;
        }

        default: {
            return {
                abstract: extractAbstract(raw),
                authors: extractAuthors(raw),
                keywords: extractKeywords(raw),
                title: extractTitle(raw),
                type,
                usability: IUsability.Unrated,
                year: extractYear(raw)
            };
        }
    }
}


// WEBPACK FOOTER //
// ./src/utils/extractBibtex.ts